import { isEqual } from 'lodash'
import { normalize, schema } from 'normalizr'
import { Reducer } from 'redux'
import { TAB } from 'apps/fleet-planner/constants/loads'
import { IAction, SORT_ORDER } from 'apps/fleet-planner/types'
import { ILoadsState, ILoad, ILoadsParams, SORT_FIELD } from 'apps/fleet-planner/types/loads'
import { UPDATE_LOAD_STATUS_ACTION_TYPES } from 'apps/fleet-planner/utils/actions/loadDetails'
import {
  FETCH_LOADS_ACTION_TYPES,
  SET_ACTIVE_TAB,
  SET_LAST_CREATED_LOAD,
} from 'apps/fleet-planner/utils/actions/loads'
import { ISuccessAction as IShipmentSuccessAction } from 'apps/fleet-planner/utils/reducers/loadDetails'
import { objToHash } from 'utils/objToHash'

const { FETCH_LOADS_FAILURE, FETCH_LOADS_REQUEST, FETCH_LOADS_SUCCESS } = FETCH_LOADS_ACTION_TYPES
const { UPDATE_LOAD_STATUS_SUCCESS } = UPDATE_LOAD_STATUS_ACTION_TYPES

interface IRequestAction extends IAction {
  payload: {
    requestData: {
      params: ILoadsParams
    }
    isFleet?: boolean
  }
}

interface ISuccessAction extends IAction {
  payload: {
    requestData: {
      params: ILoadsParams
    }
    shipments: ILoad[]
    totalCount: number
  }
}

interface ITabAction extends IAction {
  payload: {
    tab: TAB
  }
}

export interface ILastCreatedLoadAction extends IAction {
  payload: {
    load: ILoad
  }
}

type LoadsAction = IAction | IRequestAction | ISuccessAction | ITabAction | IShipmentSuccessAction

const defaultState: ILoadsState = {
  data: {
    loads: {
      byId: {},
    },
    loadIds: [],
  },
  fetching: false,
  totalCount: 0,
  activeTab: TAB.READY_FOR_DISPATCH,
  params: {
    pageSize: 20,
    pageNumber: 1,
    sortField: SORT_FIELD.PICKUP,
    sortOrder: SORT_ORDER.ASC,
    filters: {},
  },
  currentStatusFilters: '',
}

const loadsSchema = new schema.Entity('loads', {}, { idAttribute: 'id' })

const loadsReducer: Reducer<ILoadsState, LoadsAction> = (state = defaultState, action) => {
  switch (action.type) {
    case FETCH_LOADS_REQUEST: {
      const {
        payload: {
          isFleet,
          requestData: { params },
        },
      } = action as IRequestAction
      const { filters } = params || {}
      const { status } = filters || {}
      return {
        ...state,
        fetching: true,
        isFleet,
        error: undefined,
        params,
        lastCreatedLoad: undefined,
        currentStatusFilters: objToHash(status),
      }
    }

    case FETCH_LOADS_SUCCESS: {
      const {
        payload: {
          requestData: { params },
          shipments: loads = [],
          totalCount = 0,
        },
      } = action as ISuccessAction

      const { filters } = params || {}
      const { status } = filters || {}
      const { currentStatusFilters } = state

      // To prevent a possible race condition where the user switches statuses,
      // only updates if the status matches the one we are currently fetching
      if (isEqual(currentStatusFilters, objToHash(status))) {
        const { entities, result: loadIds } = normalize(loads, [loadsSchema]) || {}
        const { loads: byId = {} } = entities || {}
        return {
          ...state,
          fetching: false,
          data: { loads: { byId }, loadIds },
          totalCount,
        }
      }
      return state
    }

    case FETCH_LOADS_FAILURE: {
      return {
        ...state,
        fetching: false,
        error: action.error,
      }
    }

    case UPDATE_LOAD_STATUS_SUCCESS: {
      const {
        payload: { shipment: load },
      } = action as IShipmentSuccessAction
      return {
        ...state,
        data: {
          ...state.data,
          loads: {
            byId: {
              ...state.data.loads!.byId,
              [load.id!]: load,
            },
          },
        },
      }
    }

    case SET_ACTIVE_TAB: {
      return {
        ...state,
        activeTab: (action as ITabAction).payload.tab,
      }
    }

    case SET_LAST_CREATED_LOAD: {
      return {
        ...state,
        lastCreatedLoad: (action as ILastCreatedLoadAction).payload.load,
      }
    }

    default:
      return state
  }
}

export default loadsReducer
